import React, { useEffect } from 'lib/react';
import {
  EVPaper,
  EVGrid,
  EVTypography,
  EVButton,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import useMediaQuery from 'hooks/useMediaQuery';
import { useDispatch, useSelector } from 'lib/redux';
import Links from 'features/orderSubmitted/components/Links.component';
import RetryScreen from 'commonComponents/retry-screen';
import updateReportPitchService from 'services/updateReportPitch.service';
import PageNotFound from 'commonComponents/pageNotFound';
import {
  ERROR, LOADING, SUCCESS,
  USER_EVENTS,
} from 'constants/AppConstants';
import { scrollToTop, trackUserEvent } from 'utils/app.utils';
import ReportDetails from 'commonComponents/reportDetails/ReportDetails.component';
import PitchEditorAlert from './components/PitchEditorAlert.component';
import PitchEditorStyles from './PitchEditorStyles';
import { clearPitchEditorPage, loadPitchEditorPage, updatePitchStatus } from './PitchEditor.actions';
import EditPitches from './components/EditPitches.component';
import PitchEditorLoader from './PitchEditorLoader.component';
import { isPitchValueValid } from './PitchEditor.utils';

function PitchEditor() {
  const classes = PitchEditorStyles();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(640);
  const dispatch = useDispatch();

  const searchParams = new URLSearchParams(window.location.search);
  const reportId = searchParams.get('reportId');
  const signature = searchParams.get('signature'); // #genai

  const {
    loading, loadError, topImage, reportDetails, reportFacets, editPitchStatus, notFound,
  } = useSelector((state) => state.pitchEditor);
  const {
    decryptReportId,
  } = useSelector((state) => ({ decryptReportId: state.pitchEditor.reportId }));

  useEffect(() => {
    dispatch(loadPitchEditorPage(reportId));
    dispatch(updatePitchStatus('', ''));
    return () => dispatch(clearPitchEditorPage());
  }, []);

  const handleSaveReport = async () => {
    dispatch(updatePitchStatus(LOADING, ''));

    const requestBody = {
      facetPitches: reportFacets.reduce((obj, item) => ({ ...obj, [item.facetId]: Number(item.pitch) }), {}),
    };

    updateReportPitchService(decryptReportId, requestBody, signature)
      .then(({ ok, json }) => {
        trackUserEvent(USER_EVENTS.SAVE_PITCH);
        if (ok) {
          dispatch(updatePitchStatus(SUCCESS, json.updatedReportFileId));
        } else {
          dispatch(updatePitchStatus(ERROR, ''));
        }
        scrollToTop();
      });
  };

  if (loading) return <PitchEditorLoader />;

  if (notFound) {
    return <PageNotFound />;
  }

  if (loadError) {
    return <RetryScreen handleRetryButtonClick={() => dispatch(loadPitchEditorPage(reportId))} />;
  }

  const isAnyFacetPitchInvalid = reportFacets.some((facet) => facet.pitch === '' || !isPitchValueValid(facet.pitch));

  return (
    <>
      <PitchEditorAlert handleSaveReport={handleSaveReport} />
      <EVPaper square={isMobile} className={classes.mainPaper}>
        <EVGrid className={classes.detailsMapGrid}>
          <ReportDetails reportId={decryptReportId} reportDetails={reportDetails} reportFacets={reportFacets} />
          <img
            className={classes.topImage}
            src={`data:image/jpeg;base64,${topImage}`}
            alt={t('common__top_image')}
          />
        </EVGrid>
        <EVGrid className={classes.editPitchContainer}>
          <EVTypography className={classes.detailsHeaderTitleText}>
            {t('pitch_editor__edit_pitches')}
          </EVTypography>
          <EditPitches />
        </EVGrid>
        <EVGrid className={classes.linksAndBtn}>
          <Links />
          <EVButton
            className={classes.saveReportBtn}
            id="saveReportBtn"
            variant="contained"
            color="primary"
            onClick={() => handleSaveReport()}
            loading={editPitchStatus === LOADING}
            disabled={isAnyFacetPitchInvalid ? true : editPitchStatus === LOADING}
          >
            {t('pitch_editor__save_report')}
          </EVButton>
        </EVGrid>
      </EVPaper>
    </>
  );
}

export default PitchEditor;
